import { Component, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { NavItemEventData } from 'desy-angular';
import { CONSTANTS } from 'src/app/core/utils/constants';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  standalone: false,
})
export class HeaderComponent implements OnInit {
  
  menuItems: Array<{ label: string; link: string; relatedRoutes?: string[] }> = [];

  title = 'Plantilla logueado con título de app. Gobierno de Aragón';
  private userService = inject(UserService);
  userName$ = this.userService.userName$;
  fromJobOffers = false;

  constructor(
    private titleService: Title,
    private router: Router,
    private oidcSecurityService: OidcSecurityService,
    private route: ActivatedRoute,
  ) {}

  setDocTitle(title: string) {
    this.titleService.setTitle(title);
  }

  ngOnInit(): void {
    this.setDocTitle(this.title);

    if (!environment.securityEnabled) {
      this.buildMenu([]);
      return;
    }

    this.userService.userRoles$.subscribe(roles => {
      if (!roles || roles.length === 0) {
        const doc = this.userService.getUserDocument();
        if (doc) {
          this.userService.loadRolesFromBackend(doc).subscribe(fetchedRoles => {
            this.buildMenu(fetchedRoles); // reconstruye menú con roles del backend
          });
        }
      } else {
        this.buildMenu(roles); // roles ya existen
      }
    });

    this.route.queryParams.subscribe(params => {
      this.fromJobOffers = params['fromJobOffers'] === 'true';
    });
  }

  private buildMenu(roles: string[]) {
    // Si securityEnabled es false, mostramos todas las secciones
    if (!environment.securityEnabled) {
      this.menuItems = [
        {
          label: 'Currículum',
          link: '/curriculum',
          relatedRoutes: [
            '/curriculum',
            '/jobseeker',
            ...Object.values(CONSTANTS.ROUTES.CURRICULUM).filter(
              v => typeof v === 'string' && v.length > 0
            ),
            ...Object.values(CONSTANTS.ROUTES.JOBSEEKER).filter(
              v => typeof v === 'string' && v.length > 0
            ),
          ],
        },
        {
          label: 'Ofertas',
          link: '/job-offers',
          relatedRoutes: [
            '/job-offers',
            ...Object.values(CONSTANTS.ROUTES.JOBOFFERS).filter(
              v => typeof v === 'string' && v.length > 0
            ),
          ],
        },
        {
          label: 'Gestión',
          link: '/management',
          relatedRoutes: [
            '/management',
            ...Object.values(CONSTANTS.ROUTES.MANAGEMENT).filter(
              v => typeof v === 'string' && v.length > 0
            ),
          ],
        },
      ];
      return;
    }

    // SecurityEnabled = true → filtramos según roles
    this.menuItems = [];

    if (roles.includes('ASESOR_CV') || roles.includes('ADMIN')) {
      this.menuItems.push({
        label: 'Currículum',
        link: '/curriculum',
        relatedRoutes: [
          '/curriculum',
          '/jobseeker',
          ...Object.values(CONSTANTS.ROUTES.CURRICULUM).filter(
            v => typeof v === 'string' && v.length > 0
          ),
          ...Object.values(CONSTANTS.ROUTES.JOBSEEKER).filter(
            v => typeof v === 'string' && v.length > 0
          ),
        ],
      });
    }

    if (roles.includes('GESTOR_OFERTAS') || roles.includes('ADMIN')) {
      this.menuItems.push({
        label: 'Ofertas',
        link: '/job-offers',
        relatedRoutes: [
          '/job-offers',
          ...Object.values(CONSTANTS.ROUTES.JOBOFFERS).filter(
            v => typeof v === 'string' && v.length > 0
          ),
        ],
      });
    }

    if (roles.includes('USER_ADMIN') || roles.includes('ADMIN')) {
      this.menuItems.push({
        label: 'Gestión',
        link: '/management',
        relatedRoutes: [
          '/management',
          ...Object.values(CONSTANTS.ROUTES.MANAGEMENT).filter(
            v => typeof v === 'string' && v.length > 0
          ),
        ],
      });
    }
  }

  isActive(baseLink: string): boolean {
    const currentUrl = this.router.url;

    if (this.fromJobOffers) {
      return baseLink === '/job-offers';
    }

    // Excepción: rutas de JOBSEEKER
    if (currentUrl.includes(CONSTANTS.ROUTES.JOBSEEKER.JOBSEEKER_SEARCH_JOBOFFERS)) {
      return baseLink === '/job-offers';
    }

    if (currentUrl.includes(CONSTANTS.ROUTES.JOBSEEKER.JOBSEEKER_SEARCH)) {
      return baseLink === '/curriculum';
    }

    const menuItem = this.menuItems.find((item) => item.link === baseLink);
    if (!menuItem) return false;

    if (!menuItem.relatedRoutes || menuItem.relatedRoutes.length === 0) {
      return currentUrl === baseLink || currentUrl.startsWith(baseLink + '/');
    }

    return menuItem.relatedRoutes.some(
      (r) =>
        typeof r === 'string' &&
        r.length > 0 &&
        (currentUrl === r || currentUrl.startsWith(r + '/'))
    );
  }


  onNavItemClick(event: NavItemEventData) {
    switch (event.item.title) {
      case 'Cerrar sesión':
        this.logout();
        break;
      // otros casos
    }
  }

  logout() {
    this.oidcSecurityService.logoffAndRevokeTokens().subscribe();
  }
}
